import { Box, FormControl, Grid, InputLabel, Typography } from "@mui/material";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import FormTextField from "components/form/FormTextField";
import SelectMembership from "../../components/form/SelectMembership";
import React, { useState } from "react";
import { FormProvider, useFormContext } from "react-hook-form";
import {
  CreateRedemptionsRequest,
  defaultRedemptionsRequest,
  ofCreateRedemption,
} from "types/redemption";
import { useMembershipTypes } from "../../utils/hooks/useMembershipTypes";
import { useBranch } from "../../provider/BranchProvider";
import { IOSSwitch } from "../../components/layout/IOSSwitch";

export default function CreateRedemptions() {
  const membershipTypes = useMembershipTypes();
  const methods = useForm<CreateRedemptionsRequest>({
    defaultValues: defaultRedemptionsRequest,
  });

  const membershipTypeId = methods.watch("membershipTypeId");
  const includedCoinAmount = membershipTypes?.find(
    (m) => m.id === membershipTypeId
  )?.includedCoinAmount;

  if (membershipTypes && membershipTypes.length === 0) {
    return (
      <p>
        No Membership types found.
        <br />
        At least one membership type is required before creating any
        redemptions.
      </p>
    );
  }

  return (
    <Create
      saveButtonProps={methods.saveButtonProps}
      title={<Typography variant="h5">Create Redemptions</Typography>}
    >
      <FormProvider {...methods}>
        <CreateRedemptionsForm
          includedCoin={includedCoinAmount ? includedCoinAmount : 0}
        />
      </FormProvider>
    </Create>
  );
}

function CreateRedemptionsForm({ includedCoin }: { includedCoin: number }) {
  const { control, setValue } = useFormContext();
  const branch = useBranch();
  const [switchChecked, setSwitchChecked] = useState<boolean>(false);
  const [unlimitedChecked, setUnlimitedChecked] = useState<boolean>(false);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSwitchChecked(event.target.checked);
    setValue(ofCreateRedemption("customizeCoinAmount"), event.target.checked);
  };

  const handleUnlimitedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isUnlimited = event.target.checked;
    setUnlimitedChecked(isUnlimited);
    setValue(
      ofCreateRedemption("maxRedemptionsPerCode"),
      isUnlimited ? null : 1
    );
  };

  return (
    <Box
      component="form"
      sx={{ display: "flex", flexDirection: "column" }}
      autoComplete="off"
    >
      <FormControl variant="standard" required>
        <InputLabel>Select Membership Type</InputLabel>
        <SelectMembership name="membershipTypeId" control={control} />
      </FormControl>

      <FormTextField
        required
        control={control}
        name={ofCreateRedemption("numberOfCodes")}
        label="Amount of redemptions to create"
        type="number"
        fullWidth
        onFocus={(event: FocusEvent) => (event.target as any)?.select()}
        additionalRules={{
          min: 0,
        }}
      />

      <Grid
        container
        alignItems="center"
        style={{ display: "flex", marginTop: 16, marginBottom: 8 }}
      >
        <div>Unlimited usages</div>
        <IOSSwitch
          onChange={handleUnlimitedChange}
          checked={unlimitedChecked}
        ></IOSSwitch>
      </Grid>

      {!unlimitedChecked && (
        <FormTextField
          required
          control={control}
          name={ofCreateRedemption("maxRedemptionsPerCode")}
          label="Max usages per redemption"
          type="number"
          fullWidth
          onFocus={(event: FocusEvent) => (event.target as any)?.select()}
          additionalRules={{
            min: 1,
          }}
        />
      )}

      {branch?.coin && (
        <>
          <div>Coin Amount included in Membership: {includedCoin / 100}</div>

          <Grid container alignItems="center" style={{ display: "flex" }}>
            <div>Customize coin amount</div>
            <IOSSwitch
              onChange={handleSwitchChange}
              checked={switchChecked}
            ></IOSSwitch>
          </Grid>

          {switchChecked && (
            <FormTextField
              control={control}
              name={ofCreateRedemption("includedCoinAmount")}
              label="Included Coin"
              helperText={
                "The amount of coin a member will receive when redeeming this membership."
              }
              onFocus={(event: FocusEvent) => (event.target as any)?.select()}
              type="number"
              fullWidth
              additionalRules={{
                min: 0,
              }}
              transform={{
                input: (v: any) => {
                  if (!v) return v;
                  // TODO: use coin decimals
                  return v * 100;
                },
                output: (v: any) => {
                  if (!v) return v;
                  // TODO: use coin decimals
                  return v / 100;
                },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
}
